import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { CampaignApi, HandleError } from "@/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface CampaignMessage {
  id: number;
  communicationType: string;
  messageType: string;
  templateId: string;
  messageSentDate: string;
  messageText: string;
  campaignId: number;
}

interface Campaign {
  id: number;
  campaignName: string;
  startDate: string;
  endDate: string;
  campaignType: string;
  bonusPoints: number;
  messageConfigsCount: number;
}

export function CampaignAllCampaigns() {
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignMessages, setCampaignMessages] = useState<CampaignMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(false);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      try {
        const response = await CampaignApi.getAllCampaigns();
        const messageCounts = response.listCampaignMessage.reduce<
          Record<number, number>
        >((acc, message) => {
          acc[message.campaignId] = (acc[message.campaignId] ?? 0) + 1;
          return acc;
        }, {});

        const mappedCampaigns: Campaign[] = response.getAllCampaign.map(
          (campaign) => ({
            id: campaign.id,
            campaignName: campaign.campaignName,
            startDate: campaign.startDate,
            endDate: campaign.endDate,
            campaignType: campaign.campaignType,
            bonusPoints: campaign.bonusPoints,
            messageConfigsCount: messageCounts[campaign.id] ?? 0,
          })
        );

        setCampaigns(mappedCampaigns);
        setCampaignMessages(response.listCampaignMessage);
      } catch (error) {
        const { errorMessage } = HandleError(error);
        toast({
          title: "Error",
          description: errorMessage || "Failed to fetch campaigns",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, [toast]);

  const getCampaignTypeLabel = (campaignType: string) => {
    if (campaignType === "PrepaidPointBase") {
      return "Prepaid Points Based";
    }
    if (campaignType === "MessageOnly") {
      return "Message Only";
    }
    return campaignType;
  };

  const columns = useMemo(
    () => [
      {
        header: "Campaign ID",
        accessorKey: "id" as keyof Campaign,
      },
      {
        header: "Campaign Name",
        accessorKey: "campaignName" as keyof Campaign,
      },
      {
        header: "Campaign Type",
        id: "campaignType",
        cell: (row: Campaign) => (
          <Badge
            variant={row.campaignType === "PrepaidPointBase" ? "default" : "secondary"}
            className="flex items-center gap-1 w-fit"
          >
            {getCampaignTypeLabel(row.campaignType)}
          </Badge>
        ),
      },
      {
        header: "Bonus Points",
        id: "bonusPoints",
        cell: (row: Campaign) => row.bonusPoints.toString(),
      },
      {
        header: "Start Date",
        id: "startDate",
        cell: (row: Campaign) => format(new Date(row.startDate), "PPP"),
      },
      {
        header: "End Date",
        id: "endDate",
        cell: (row: Campaign) => format(new Date(row.endDate), "PPP"),
      },
      {
        header: "Messages Count",
        id: "messageConfigsCount",
        cell: (row: Campaign) => (
          <span className="text-gray-700">{row.messageConfigsCount}</span>
        ),
      },
      {
        header: "Action",
        id: "action",
        cell: (row: Campaign) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSelectedCampaign(row);
              setIsMessagesModalOpen(true);
            }}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const selectedCampaignMessages = useMemo(() => {
    if (!selectedCampaign) {
      return [];
    }
    return campaignMessages.filter(
      (message) => message.campaignId === selectedCampaign.id
    );
  }, [campaignMessages, selectedCampaign]);

  return (
    <div className="space-y-6">
      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-800">All Campaigns</h2>
          </div>
          <DataTable
            title=""
            data={campaigns}
            cellClassName="py-6 text-gray-700"
            columns={columns}
          />
        </div>
      )}

      <Dialog open={isMessagesModalOpen} onOpenChange={setIsMessagesModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Campaign Messages{" "}
              {selectedCampaign ? `- ${selectedCampaign.campaignName}` : ""}
            </DialogTitle>
          </DialogHeader>

          {selectedCampaignMessages.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No messages available for this campaign.
            </p>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto space-y-3 pr-1">
              {selectedCampaignMessages.map((message) => (
                <div key={message.id} className="rounded-md border p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">
                      {message.messageType}
                    </span>
                    <Badge variant="secondary">{message.communicationType}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Template ID: {message.templateId}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Sent Date: {format(new Date(message.messageSentDate), "PPP p")}
                  </div>
                  <p className="text-sm text-gray-800">{message.messageText}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
